import { FormattedMessageDiv, LoadingAnimation, useWizardContext } from '@odmonorepo/core-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { patchAppDataOpts, unifiedApiClient, useApplication } from 'merchant-api/unified';
import type { paths } from 'merchant-api/unified/oas';
import { useEffect, useRef } from 'react';
import { Stack } from 'react-bootstrap';
import { useAnalytics } from 'use-analytics';
import * as rfc6902 from 'rfc6902';
import { hasProvePrefillConsent, markProveBusinessPrefill } from '../helpers';
import { BusinessSelect, type BusinessOption } from './BusinessSelect';

type ProveBusinessMatchesResponse = paths['/client/prove/business-matches']['post']['responses'][200]['content']['application/json'];
type ProveBusinessMatch = ProveBusinessMatchesResponse['businesses'][number];

const upsertByType = <Existing extends { type?: string }, Update extends { type?: string }>(
  existing: Existing[] = [],
  updates: Update[] = [],
) => [
  ...existing.map((entry) => ({ ...entry, ...updates.find((update) => update.type === entry.type) })),
  ...updates.filter((update) => !existing.some((entry) => entry.type === update.type)),
];

const toBusinessOption = (business: ProveBusinessMatch, index: number): BusinessOption<ProveBusinessMatch> => {
  const physical = business.addresses?.find((address) => address.type === 'PHYSICAL_ADDRESS');
  return {
    id: business.id ?? `prove-match-${index}`,
    legalName: business.legalName ?? business.doingBusinessAs ?? '',
    businessAddress: physical
      ? { address: physical.lineOne, city: physical.city, region: physical.state, postalCode: physical.postalCode }
      : undefined,
    inceptionDate: business.inceptionDate,
    details: business,
  };
};

export const BusinessSelectStep = () => {
  const { applicationData } = useApplication();
  const { nextStep, previousStep, skipStep, markStepLoaded } = useWizardContext();
  const analytics = useAnalytics();
  const skippedRef = useRef(false);
  const stepLoadedRef = useRef(false);

  // The application's provePrefillConsent is written only by the Prove landing variant, which
  // is itself flag-gated — so the consent alone proves this flow was bucketed into prove. An
  // earlier version re-read the Optimizely decision from the query cache here (enabled: false),
  // but that cache is in-memory only: any reload or the logout cache clear emptied it and
  // silently skipped the step for applications that legitimately carried consent.
  const consent = hasProvePrefillConsent(applicationData ?? undefined);
  // Only skip once the application has loaded and provably lacks consent; while it is still
  // loading we show the spinner instead of advancing past the step.
  const skip = !!applicationData && !consent;

  // Prove business look-ups are billable, so cache the matches for the life of the session and
  // never retry: any failure just skips the step instead of blocking the application. The global
  // query default throws errors to the boundary (which re-triggers login on 401/403), so this
  // query must keep its errors local for the skip to happen.
  const { data, isError } = useQuery({
    queryKey: ['proveBusinessMatches', applicationData?.id],
    queryFn: async () => {
      const { data } = await unifiedApiClient.POST('/client/prove/business-matches', { body: { appId: applicationData?.id } as never });
      return data ?? { businesses: [] };
    },
    enabled: consent && !!applicationData?.id,
    staleTime: Infinity,
    retry: false,
    throwOnError: false,
  });

  const businesses = data?.businesses ?? [];
  const shouldSkip = skip || isError || (!!data && businesses.length === 0);

  // skipStep (not nextStep): an applicant who arrived here with Back keeps going back, instead of being
  // bounced forward to the step they just left, which made Back look dead on the next step.
  useEffect(() => {
    if (shouldSkip && !skippedRef.current) {
      skippedRef.current = true;
      skipStep();
    }
  }, [shouldSkip, skipStep]);

  // Emitted here (not by the Wizard) so a self-skipped step logs no view. Only exists in direct_prove_prefill.
  useEffect(() => {
    if (!stepLoadedRef.current && data && businesses.length > 0) {
      stepLoadedRef.current = true;
      markStepLoaded('BUSINESS_SELECT');
      analytics.track('StepLoaded', { step: 'BUSINESS_SELECT', flowType: 'direct_prove_prefill', stepOrder: 3 });
    }
  }, [analytics, data, businesses.length, markStepLoaded]);

  const { mutateAsync: patch } = useMutation({
    ...patchAppDataOpts(),
    onSuccess: () => {
      // Session-scoped: the following business steps read this to render their review variants.
      if (applicationData) markProveBusinessPrefill(applicationData.id, true);
      nextStep();
    },
  });

  const onSelect = async ({ details }: BusinessOption<ProveBusinessMatch>) => {
    if (!applicationData) return;
    const existingBusinesses = applicationData.data?.party?.businesses ?? [];
    const current = existingBusinesses[0];
    const identifiers = upsertByType(current?.identifiers, details.identifiers);
    const addresses = upsertByType(current?.addresses, details.addresses);
    const selected = {
      ...current,
      ...details,
      ...(identifiers.length ? { identifiers } : {}),
      ...(addresses.length ? { addresses } : {}),
    };
    const output = {
      ...applicationData.data,
      party: {
        ...applicationData.data?.party,
        businesses: [selected, ...existingBusinesses.slice(1)],
      },
    };
    const patches = rfc6902.createPatch(applicationData.data, output);
    await patch({ id: applicationData.id, patches });
  };

  const onNoneOfTheAbove = () => {
    // Re-selecting "none of the above" after going back reverts the review variants.
    if (applicationData) markProveBusinessPrefill(applicationData.id, false);
    nextStep();
  };

  if (shouldSkip || !data) return <div className="mx-auto"><LoadingAnimation /></div>;

  return (
    <Stack gap={5}>
      <FormattedMessageDiv className="h3 text-center mb-0" id="businessSelect.header" />
      <BusinessSelect
        businesses={businesses.map(toBusinessOption)}
        onSelect={onSelect}
        onNoneOfTheAbove={onNoneOfTheAbove}
        onBack={() => previousStep()}
      />
    </Stack>
  );
};

export default BusinessSelectStep;
